import { Injectable } from 'angular-ts-decorators';

import { pull } from 'lodash';
import { RouteStreams } from './route-streams';
import { find } from 'lodash';

/**
 * Class that represents the notifications used in the notification manager
 */

//  TODO rename to avoid confusion with typescript native notification type
export class Notification {
  removePromise: any;
  _notificationDefer: any;

  constructor(
    $q: ng.IQService,
    $interval: ng.IIntervalService,
    private notificationManager: NotificationManager,

    public readonly type: string,
    public readonly message: string,
    public readonly id: string,
    public readonly lifeTime: number,
    public error?: Error,
  ) {
    this._notificationDefer = $q.defer();
    this.removePromise = this._notificationDefer.promise;

    if (this.lifeTime > 0) {
      $interval(
        () => {
          this.remove();
        },
        this.lifeTime,
        1,
      );
    }
  }

  /**
   * Remove the notification from the manager and resolve the promise
   * @return {void}
   */
  remove(): void {
    this._notificationDefer.resolve(true);
    this.notificationManager.removeNotification(this);
  }
}

/**
 * Service that takes care of user notification. It's non active for anonymous
 * user.
 */
@Injectable('notificationManager')
export class NotificationManager {
  public readonly defaultLifeTime: number;
  private _collection: Notification[];

  constructor(
    private $q: ng.IQService,
    private $interval: ng.IIntervalService,
    private uuid,
    private routeStreams: RouteStreams,
  ) {
    'ngInject';

    this.defaultLifeTime = 5000;
    this._collection = [];
  }

  /**
   *
   * Start listening on '$routeChangeStart' event and clear the current notifications if fired
   * @return {Void}
   * Nothing
   */
  init(): void {
    this.routeStreams.changeStart$.subscribe(() => {
      this._collection.forEach((notification) => {
        if (notification.lifeTime !== 0) {
          notification.remove();
        }
      });
    });
  }

  get collection() {
    return this._collection;
  }

  removeNotification(notification: Notification): void {
    pull(this._collection, notification);
  }

  createNotificationUUID(): string {
    return this.uuid.v4();
  }

  /**
   * Sends a notification with the corresponding message, kebab-case preferred
   * @param {string} type of notification as string
   * @param {string} message the message the notification should contain
   * @param {string} id the id of the message which which will default to a new UUID if empty
   * @param {string} lifeTime the amount of time a notification should be present defaults to 5s
   * @param {error} error
   * Error we've got, one that makes us show the notificiaton
   * @return {Void}
   * Nothing
   */
  private sendNotification(
    type: string,
    message: string,
    id?: string,
    lifeTime?: number,
    error?: Error,
  ): Notification {
    const hasMessage = find(this._collection, {
      id: id,
    });
    if (!hasMessage) {
      let notification = new Notification(
        this.$q,
        this.$interval,
        this,
        type,
        message,
        id || this.uuid.v4(),
        angular.isUndefined(lifeTime) ? this.defaultLifeTime : lifeTime,
        error,
      );
      this._collection.push(notification);
      return notification;
    }

    return null;
  }

  /**
   *
   * Sends a notification with the given message and type as success
   * @param {string} message
   * Message that needs to be displayed in the notification
   * @param {string} id
   * Identifier to lookup certain notifications if needed
   * @param {integer} lifeTime
   * Life time of the notification, defaults to 5000, 0 means infinite
   * @return {Notification}
   * Notification object or false if id of the notification already exists
   */
  sendSuccess(message: string, id?: string, lifeTime?: number): Notification {
    return this.sendNotification('success', message, id, lifeTime);
  }

  /**
   *
   * Sends a notification with the given message and type as info
   * @param {string} message
   * Message that needs to be displayed in the notification
   * @param {string} id
   * Identifier to lookup certain notifications if needed, kebab-case preferred
   * @param {integer} lifeTime
   * Life time of the notification, defaults to 5000, 0 means infinite
   * @return {Notification}
   * Notification object or false if id of the notification already exists
   */
  sendInfo(message: string, id?: string, lifeTime?: number): Notification {
    return this.sendNotification('info', message, id, lifeTime);
  }

  /**
   *
   * Sends a notification with the given message and type as warning
   * @param {string} message
   * Message that needs to be displayed in the notification
   * @param {string} id
   * Identifier to lookup certain notifications if needed, kebab-case preferred
   * @param {integer} lifeTime
   * Life time of the notification, defaults to 5000, 0 means infinite
   * @return {Notification}
   * Notification object or false if id of the notification already exists
   */
  sendWarning(message: string, id?: string, lifeTime?: number): Notification {
    return this.sendNotification('warning', message, id, lifeTime);
  }

  /**
   *
   * Sends a notification with the given message and type as error
   * @param {string} message
   * Message that needs to be displayed in the notification
   * @param {string} id
   * Identifier to lookup certain notifications if needed, kebab-case preferred
   * @param {integer} lifeTime
   * Life time of the notification, defaults to 5000, 0 means infinite
   * @param {error} error
   * Error we've got, that makes us to show the notificiaton
   * @return {Notification}
   * Notification object or false if id of the notification already exists
   */
  sendError(
    message: string,
    id?: string,
    lifeTime?: number,
    error?: Error,
  ): Notification {
    return this.sendNotification('error', message, id, lifeTime, error);
  }
}
